import React, { useState } from 'react';
import { useReportWidgetData } from '../../useReportWidgetData';
import Loader from 'src/components/shared/loader/loader';
import {
    ExecutiveSummaryTable,
    ExecutiveSummaryTableColumn
} from '@src/components/features/reports-and-logs/executive-summary/ExecutiveSummaryTable/ExecutiveSummaryTable';
import ArrowRight from '@svg/arrow-right-blue.svg?react';
import { Link } from 'react-router-dom';
import CustomIcon, { IconType } from 'src/components/shared/custom-icon/custom-icon';
import DirectionSelect from 'src/components/shared/DirectionSelect/DirectionSelect';
import { BarChartBar } from 'src/components/shared/BarChartBar/BarChartBar';
import { BaseWidgetProps } from '../types';
import { Direction, ReportName } from '@src/utils/enums';
import TrendPercentIndicator from '@src/components/shared/TrendPercentIndicator/TrendPercentIndicator';
import './BlockedSessionsByTypeWidget.scss';
import { formatNumber } from '@src/utils/number-util';

type BlockedSessionType = 'Apps' | 'Web Filtering' | 'Geo Filtering' | 'Services' | 'Dynamic Defense Shield' | 'Premium threat protection' | 'DNS Protection';

interface BlockedSessionsByTypeRow {
    type: BlockedSessionType;
    count: number;
    trendPercent: number;
}

const TYPE_CONFIG: Record<BlockedSessionType, {
    icon: IconType,
    reportName: ReportName,
    showNavOn: Direction[]
}> = {
    'Apps': {
        icon: IconType.APPS_LOGO,
        reportName: ReportName.TOP_15_BLOCKED_APPS,
        showNavOn: [Direction.Inbound, Direction.Outbound]
    },
    'Web Filtering': {
        icon: IconType.WEB_FILTERING,
        reportName: ReportName.TOP_BLOCKED_URL,
        showNavOn: [Direction.Outbound]
    },
    'Geo Filtering': {
        icon: IconType.GEO_FILTERING,
        reportName: ReportName.TOP_15_BLOCKED_COUNTRIES,
        showNavOn: [Direction.Inbound, Direction.Outbound]
    },
    'Services': {
        icon: IconType.SERVICES_LOGO,
        reportName: ReportName.TOP_15_BLOCKED_SERVICES,
        showNavOn: [Direction.Inbound, Direction.Outbound]
    },
    'Dynamic Defense Shield': {
        icon: IconType.DD_SHIELD,
        reportName: ReportName.TOP_GLOBAL_BLOCK_OUTBOUND_HITS,
        showNavOn: [Direction.Outbound]
    },
    'Premium threat protection': {
        icon: IconType.PROTECTION_PLUS,
        reportName: ReportName.TOP_15_DETECTED_THREATS,
        showNavOn: [Direction.Inbound, Direction.Outbound]
    },
    'DNS Protection': {
        icon: IconType.PROTECTION_PLUS,
        reportName: ReportName.TOP_BLOCKED_DOMAINS,
        showNavOn: [Direction.Outbound]
    },
};

const BST_TABLE_COLUMNS: ExecutiveSummaryTableColumn[] = [
  { key: 'type', label: 'Type' },
  { key: 'count', label: 'Count' },
  { key: 'trend', label: '' },
  { key: 'block', label: '% Blocks' },
  { key: 'bar', label: '' },
  { key: 'nav', label: '' },
];

export const BlockedSessionsByTypeWidget: React.FC<BaseWidgetProps> = ({ fromDate, toDate, customerId, serviceId }) => {
  const [direction, setDirection] = useState<Direction>(Direction.Inbound);

  const { data, loading, error } = useReportWidgetData({
    fromDate,
    toDate,
    reportName: 'blockedSessionsByType',
    direction,
    customerId,
    serviceId,
  });

  const rows: BlockedSessionsByTypeRow[] = (data as BlockedSessionsByTypeRow[]) || [];
  const maxCount = rows.length > 0 ? Math.max(...rows.map(r => r.count)) : 1;
  const totalCount = rows.reduce((sum, r) => sum + r.count, 0) || 1;

  const renderRow = (row: BlockedSessionsByTypeRow) => {
      const { icon, reportName, showNavOn } = TYPE_CONFIG[row.type];
      const showNav = showNavOn.includes(direction);
    
    return (
      <div className="es-tr" key={row.type}>
        <div className="es-td bst-td-type">
          <CustomIcon type={icon} className="bst-icon" />
          <span className="bst-type-label">{row.type}</span>
        </div>
        <div className="es-td bst-td-count">
            <div>{formatNumber(row.count)}</div>
        <TrendPercentIndicator
          trendPercent={row.trendPercent}
          showTrendDirectionText={false} />
        </div>
        <div className="es-td bst-td-block">{((row.count / totalCount) * 100).toFixed(1)}%</div>
        <BarChartBar bgClassName="es-td" widthPercent={maxCount > 0 ? (row.count / maxCount) * 100 : 0} />
        <div className="es-td bst-td-nav">
            {showNav ? (
                <Link className="circle-nav"
                      to={`/${customerId}/${serviceId}/insights/reports/${reportName}`}
                      state={{ direction }}>
                    <ArrowRight/>
                </Link>
            ) : null}
        </div>
      </div>
    );
  };

  if (loading) {
    return <Loader type="component" />;
  }

  return (
    <div className="blocked-sessions-by-type-widget">
      <div className="bst-header">
        <div className="widget-title">Blocked sessions by type</div>
        <DirectionSelect value={direction} onChange={value => setDirection(value as Direction)} />
      </div>
      <ExecutiveSummaryTable
        columns={BST_TABLE_COLUMNS}
        data={loading ? [] : rows}
        renderRow={renderRow}
        className="bst-table"
        headerContent={loading ? <Loader type="component" /> : undefined}
        hasError={!!error}
      />
    </div>
  );
};
